import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import CustomFieldsForm from "go-app/components/CustomFields/CustomFieldsForm";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { OrganizationApi } from "../../../../../../../../../../../services/Api/types";
import { api } from "../../../../../../../../../../../services/Security/api";
import { selectOrganization } from "../../../../../../../../../../../services/organizations/selectors";
import { OrganizationActionTypes } from "../../../../../../../../../../../services/organizations/types";
import { UserActionTypes } from "../../../../../../../../../../../services/users/types";
import OrganizationAddressForm from "./OrganizationAddressForm";
import OrganizationContactForm from "./OrganizationContactForm";

interface Props {
	details: OrganizationApi;
	customFieldsConfig: CustomFieldTemplateApi[];
}

const OrganizationBasicInfoForm = ({ details, customFieldsConfig }: Props) => {
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const form = useForm<OrganizationApi>({
		criteriaMode: "all",
		defaultValues: {
			name: details.name,
			place: {
				country: details.place?.country,
				zip_code: details.place?.zip_code,
				city: details.place?.city,
				street: details.place?.street,
				build_nr: details.place?.build_nr,
				flat_nr: details.place?.flat_nr,
				website: details.place?.website,
				facebook: details.place?.facebook?.replace("fb.com/", ""),
				instagram: details.place?.instagram?.replace("instagram.com/", ""),
				linkedin: details.place?.linkedin?.replace("linkedin.com/company/", ""),
			},
			currency: details.currency,
			timezone: details.timezone,
			custom_fields: mapCustomFieldsToForm(customFieldsConfig, details.custom_fields),
		},
		shouldUnregister: false,
	});
	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		formState,
		reset,
	} = form;
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const [loading, setLoading] = useState<boolean>(false);
	const dispatch = useDispatch();

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) {
			return;
		}

		setLoading(true);
		try {
			if (organization && organization.id) {
				const params: Record<string, any> = {};
				params.include = "place,custom_fields";
				const res = await api.user().updateOrganizationMePatch(organization.id, data, params);
				const resData = res.data.data;

				const organizationData = {
					name: resData.name,
					address: {
						street: resData.street,
						build_nr: resData.build_nr,
						flat_nr: resData.flat_nr,
						zip_code: resData.zip_code,
						city: resData.city,
						country: resData.country,
					},
				};

				dispatch({
					type: UserActionTypes.UPDATE_ORGANIZATION,
					payload: { ...organizationData },
				});
				dispatch({
					type: OrganizationActionTypes.UPDATE,
					payload: { ...organizationData },
				});

				reset({
					...res.data.data,
					place: {
						...res.data.data.place,
						facebook: res.data.data.place?.facebook?.replace("fb.com/", ""),
						instagram: res.data.data.place?.instagram?.replace("instagram.com/", ""),
						linkedin: res.data.data.place?.linkedin?.replace("linkedin.com/company/", ""),
					},
					custom_fields: mapCustomFieldsToForm(customFieldsConfig, resData.custom_fields),
				});
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			}
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<FormDirty formState={formState} loading={loading} onSubmit={onSubmit}>
			<fieldset className="form-group no-gutters">
				<div className="w-100">
					<FormInput label={t("lib:common.word.name")} register={register} name="name" errors={errors} />
				</div>
				<OrganizationAddressForm form={form} />
				<OrganizationContactForm form={form} />
				<hr />
				<div>
					<h5>{t("common.word.advanced", { ns: "lib" })}</h5>
					<CustomFieldsForm
						form={form}
						customFieldsConfig={customFieldsConfig}
						customFields={details.custom_fields}
						setErrors={setErrors}
					/>
				</div>
			</fieldset>
		</FormDirty>
	);
};

export default OrganizationBasicInfoForm;
